import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Chip,
  CardMedia,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Stack,
  alpha,
  Collapse
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalOffer as LocalOfferIcon,
  ExpandMore as ExpandMoreIcon,
  Collections as CollectionsIcon,
  Palette as PaletteIcon,
  Straighten as StraightenIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createProduct, getSupportedCurrencies } from '../productsApi';
import { fetchCategories } from '../categoriesApi';
import { useBranch } from '../BranchContext';

export default function AddCustomProduct() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [step, setStep] = useState(1);
  const [currencyPrices, setCurrencyPrices] = useState({ YER: '' });
  const [product, setProduct] = useState({
    ProductName: '',
    Description: '',
    CategoryId: '',
    IsActive: true,
    Ingredients: '',
    SubProducts: []
  });
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [helpOpen, setHelpOpen] = useState(false);
  const [expandedSubProducts, setExpandedSubProducts] = useState({});
  const { selectedBranch } = useBranch();

  const [productImageFiles, setProductImageFiles] = useState([]);
  const [productImagePreviews, setProductImagePreviews] = useState([]);

  const [cpAccountActive, setCpAccountActive] = useState(true);

  const [productColors, setProductColors] = useState([]);
  const [currentColor, setCurrentColor] = useState({ 
    colorName: '', 
    colorImage: null, 
    colorImagePreview: null 
  });

  // ✅ NEW: Sizes state
  const [productSizes, setProductSizes] = useState([]);
  const [currentSizeName, setCurrentSizeName] = useState('');

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        setCurrencies(data.Currencies || []);
      } catch (error) {
        console.error('Error loading currencies:', error);
        setCurrencies([
          { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
          { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
          { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
          { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 }
        ]);
      }
    };
    loadCurrencies();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      if (!selectedBranch) return;
      try {
        const data = await fetchCategories(selectedBranch.id || selectedBranch.Id);
        const filtered = data.filter(c => {
          const name = c.categoryName || c.CategoryName || '';
          return !name.startsWith("العروض:") && 
                 !name.startsWith("مجموعة:") &&
                 name !== "Offer" &&
                 name !== "Deal";
        });
        setCategories(filtered);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const handleCategorySelect = (category) => {
    setProduct({ ...product, CategoryId: category.id || category.Id });
    setSelectedCategoryName(category.categoryName || category.CategoryName);
    setStep(2);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
      setImagePreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleRemoveMainImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setNotification({ open: true, message: t('mainImageRemoved') || 'Main image removed', severity: 'info' });
  };

  const handleProductImagesChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).slice(0, 8);
      setProductImageFiles(files);
      const previews = files.map(file => URL.createObjectURL(file));
      setProductImagePreviews(previews);
      if (e.target.files.length > 8) {
        setNotification({ open: true, message: t('maxProductImagesWarning') || 'Maximum 8 images allowed', severity: 'warning' });
      }
    }
  };

  const handleRemoveProductImage = (index) => {
    setProductImageFiles(productImageFiles.filter((_, i) => i !== index));
    setProductImagePreviews(productImagePreviews.filter((_, i) => i !== index));
  };

  const handleColorNameChange = (e) => {
    setCurrentColor({ ...currentColor, colorName: e.target.value });
  };

  const handleColorImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCurrentColor({
        ...currentColor,
        colorImage: file,
        colorImagePreview: URL.createObjectURL(file)
      });
    }
  };

  const handleAddColor = () => {
    if (!currentColor.colorName.trim()) {
      setNotification({ open: true, message: t('colorNameRequired') || 'Please enter color name', severity: 'warning' });
      return;
    }
    if (productColors.some(c => c.colorName.toLowerCase() === currentColor.colorName.toLowerCase())) {
      setNotification({ open: true, message: t('colorAlreadyExists') || 'Color already exists', severity: 'warning' });
      return;
    }
    setProductColors([...productColors, { ...currentColor }]);
    setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
    setNotification({ open: true, message: t('colorAdded') || `Color "${currentColor.colorName}" added`, severity: 'success' });
  };

  const handleRemoveColor = (index) => {
    const removed = productColors[index];
    setProductColors(productColors.filter((_, i) => i !== index));
    setNotification({ open: true, message: t('colorRemoved') || `Color "${removed.colorName}" removed`, severity: 'info' });
  };

  // ✅ NEW: Size handlers
  const handleAddSize = () => {
    const name = currentSizeName.trim();
    if (!name) {
      setNotification({ open: true, message: t('sizeNameRequired') || 'Please enter size name', severity: 'warning' });
      return;
    }
    if (productSizes.some(s => s.sizeName.toLowerCase() === name.toLowerCase())) {
      setNotification({ open: true, message: t('sizeAlreadyExists') || 'Size already exists', severity: 'warning' });
      return;
    }
    setProductSizes([...productSizes, { sizeName: name }]);
    setCurrentSizeName('');
    setNotification({ open: true, message: t('sizeAdded') || `Size "${name}" added`, severity: 'success' });
  };

  const handleRemoveSize = (index) => {
    const removed = productSizes[index];
    setProductSizes(productSizes.filter((_, i) => i !== index));
    setNotification({ open: true, message: t('sizeRemoved') || `Size "${removed.sizeName}" removed`, severity: 'info' });
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setProduct({ ...product, [name]: type === 'checkbox' ? checked : value });
  };

  const handleAddCurrency = (currency) => {
    if (!currencyPrices[currency]) {
      setCurrencyPrices({ ...currencyPrices, [currency]: '' });
    }
  };

  const handleRemoveCurrency = (currency) => {
    const updated = { ...currencyPrices };
    delete updated[currency];
    setCurrencyPrices(updated);
  };

  const handlePriceChange = (currency, value) => {
    setCurrencyPrices({ ...currencyPrices, [currency]: value });
  };

  const getCurrencyInfo = (code) => {
    return currencies.find(c => c.code === code) || { code, symbol: code, name: code, decimals: 2 };
  };

  const handleAddSubProduct = () => {
    const newIndex = product.SubProducts.length;
    setProduct({
      ...product,
      SubProducts: [
        ...product.SubProducts,
        { 
          Name: '', 
          Description: '', 
          CurrencyPrices: { YER: '' },
          SizeType: '', 
          Height: '', 
          Width: '', 
          Image: '', 
          IsActive: true, 
          FixedQuantity: false 
        }
      ]
    });
    if (isMobile) {
      setExpandedSubProducts(prev => ({ ...prev, [newIndex]: true }));
    }
  };

  const handleSubProductChange = (idx, field, value) => {
    const updated = product.SubProducts.map((sp, i) =>
      i === idx ? { ...sp, [field]: value } : sp
    );
    setProduct({ ...product, SubProducts: updated });
  };

  const handleAddSubProductCurrency = (idx, currency) => {
    const updated = [...product.SubProducts];
    if (!updated[idx].CurrencyPrices[currency]) {
      updated[idx].CurrencyPrices = { ...updated[idx].CurrencyPrices, [currency]: '' };
      setProduct({ ...product, SubProducts: updated });
    }
  };

  const handleRemoveSubProductCurrency = (idx, currency) => {
    const updated = [...product.SubProducts];
    const newPrices = { ...updated[idx].CurrencyPrices };
    delete newPrices[currency];
    updated[idx].CurrencyPrices = newPrices;
    setProduct({ ...product, SubProducts: updated });
  };

  const handleSubProductPriceChange = (idx, currency, value) => {
    const updated = [...product.SubProducts];
    updated[idx].CurrencyPrices = { ...updated[idx].CurrencyPrices, [currency]: value };
    setProduct({ ...product, SubProducts: updated });
  };

  const handleSubProductImage = (idx, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      handleSubProductChange(idx, 'Image', e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSubProductImage = (idx) => {
    handleSubProductChange(idx, 'Image', '');
    setNotification({ open: true, message: t('subproductImageRemoved') || 'Subproduct image removed', severity: 'info' });
  };

  const handleRemoveSubProduct = (idx) => {
    setProduct({ ...product, SubProducts: product.SubProducts.filter((_, i) => i !== idx) });
    const newExpanded = { ...expandedSubProducts };
    delete newExpanded[idx];
    setExpandedSubProducts(newExpanded);
  };

  const toggleSubProductExpand = (idx) => {
    setExpandedSubProducts(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) {
      setNotification({ open: true, message: t('selectBranchFirst'), severity: 'warning' });
      return;
    }

    const validMainPrices = Object.entries(currencyPrices).filter(([_, price]) => price && parseFloat(price) > 0);
    
    if (!product.ProductName || validMainPrices.length === 0 || !product.CategoryId) {
      setNotification({ open: true, message: t('fillRequiredFields'), severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      let imageJson = null;
      if (imageFile) {
        const reader = new FileReader();
        imageJson = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(imageFile);
        });
      }

      let productImagesArray = [];
      if (productImageFiles.length > 0) {
        for (const file of productImageFiles) {
          const reader = new FileReader();
          const base64 = await new Promise((resolve) => {
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(file);
          });
          productImagesArray.push(base64);
        }
      }

      let productColorsArray = [];
      if (productColors.length > 0) {
        for (const color of productColors) {
          let colorImageBase64 = null;
          if (color.colorImage) {
            const reader = new FileReader();
            colorImageBase64 = await new Promise((resolve) => {
              reader.onload = (e) => resolve(e.target.result);
              reader.readAsDataURL(color.colorImage);
            });
          }
          productColorsArray.push({ colorName: color.colorName, colorImage: colorImageBase64 });
        }
      }

      // ✅ NEW: Sizes payload
      const productSizesArray = productSizes.map(size => ({
        sizeName: size.sizeName
      }));

      const mainCurrencyPrices = {};
      validMainPrices.forEach(([currency, price]) => {
        mainCurrencyPrices[currency] = parseFloat(price);
      });

      const subProductsPayload = product.SubProducts.map(sp => {
        const subCurrencyPrices = {};
        Object.entries(sp.CurrencyPrices || {}).forEach(([currency, price]) => {
          if (price && parseFloat(price) > 0) {
            subCurrencyPrices[currency] = parseFloat(price);
          }
        });

        return {
          Name: sp.Name,
          Description: sp.Description,
          CurrencyPrices: subCurrencyPrices,
          SizeType: sp.SizeType,
          Height: parseInt(sp.Height) || 0,
          Width: parseInt(sp.Width) || 0,
          Image: sp.Image,
          IsActive: sp.IsActive,
          FixedQuantity: sp.FixedQuantity
        };
      });

      const payload = {
        ProductName: product.ProductName,
        Description: product.Description,
        CurrencyPrices: mainCurrencyPrices,
        CategoryId: parseInt(product.CategoryId),
        IsActive: product.IsActive,
        Ingredients: product.Ingredients,
        ImageJson: imageJson,
        ProductImages: productImagesArray.length > 0 ? productImagesArray : null,
        ProductColors: productColorsArray.length > 0 ? productColorsArray : null,
        ProductSizes: productSizesArray.length > 0 ? productSizesArray : null, // ✅ NEW
        BranchId: selectedBranch.id || selectedBranch.Id,
        SubProducts: subProductsPayload,
        CPAccountActive: product.SubProducts.length > 0 ? cpAccountActive : null // ✅ NEW
      };

      await createProduct(payload);
      setNotification({ open: true, message: t('productCreatedSuccess'), severity: 'success' });
      
      setProduct({ ProductName: '', Description: '', CategoryId: '', IsActive: true, Ingredients: '', SubProducts: [] });
      setCurrencyPrices({ YER: '' });
      setImageFile(null);
      setImagePreview(null);
      setProductImageFiles([]);
      setProductImagePreviews([]);
      setProductColors([]);
      setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
      setProductSizes([]); // ✅ NEW
      setCurrentSizeName(''); // ✅ NEW
      setSelectedCategoryName('');
      setExpandedSubProducts({});
      setStep(1);
    } catch (error) {
      console.error('Error creating product:', error);
      setNotification({ open: true, message: t('createProductError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ COMPLETE Help Dialog
  const HelpDialog = () => {
    const steps = [
      {
        label: t('helpCustomStep1Label') || 'Step 1: Select Category',
        icon: <CategoryIcon color="primary" />,
        description: t('helpCustomStep1Description') || 'Choose the category for your custom product',
        tips: [
          t('helpCustomStep1Tip1') || 'Categories help organize products',
          t('helpCustomStep1Tip2') || 'Click any category to proceed',
          t('helpCustomStep1Tip3') || 'You can change later'
        ]
      },
      {
        label: t('helpCustomStep2Label') || 'Step 2: Main Product Details',
        icon: <InventoryIcon color="primary" />,
        description: t('helpCustomStep2Description') || 'Fill in main product information',
        tips: [
          t('helpCustomStep2Tip1') || 'Name and price are required',
          t('helpCustomStep2Tip2') || 'Add prices in multiple currencies',
          t('helpCustomStep2Tip3') || 'Description helps customers',
          t('helpCustomStep2Tip4') || 'Upload a main product image',
          t('helpCustomStep2Tip5') || 'Add up to 8 additional product images'
        ]
      },
      {
        label: t('helpCustomStep3Label') || 'Step 3: Upload Images',
        icon: <PhotoCameraIcon color="primary" />,
        description: t('helpCustomStep3Description') || 'Add product images',
        tips: [
          t('helpCustomStep3Tip1') || 'High quality images work best',
          t('helpCustomStep3Tip2') || 'Each subproduct can have an image',
          t('helpCustomStep3Tip3') || 'Preview before saving',
          t('helpCustomStep3Tip4') || 'Main product can have up to 8 gallery images'
        ]
      },
      {
        label: t('helpCustomStep4Label') || 'Step 4: Add Subproducts',
        icon: <LocalOfferIcon color="primary" />,
        description: t('helpCustomStep4Description') || 'Add customization options',
        tips: [
          t('helpCustomStep4Tip1') || 'Subproducts = sizes, toppings, extras',
          t('helpCustomStep4Tip2') || 'Each can have different prices',
          t('helpCustomStep4Tip3') || 'Multi-currency supported per subproduct',
          t('helpCustomStep4Tip4') || 'Fixed quantity = customer cannot change qty'
        ]
      },
      {
        label: t('helpCustomStep5Label') || 'Step 5: Save Product',
        icon: <CheckCircleIcon color="success" />,
        description: t('helpCustomStep5Description') || 'Review and create',
        tips: [
          t('helpCustomStep5Tip1') || 'Double check all details',
          t('helpCustomStep5Tip2') || 'All subproducts will be saved',
          t('helpCustomStep5Tip3') || 'Product goes live immediately'
        ]
      }
    ];

    return (
      <Dialog 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
          <HelpIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6">{t('helpCustomDialogTitle') || 'How to Add Custom Products'}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={() => setHelpOpen(false)} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              {t('helpWhatIsCustomProduct') || 'What are Custom Products?'}
            </Typography>
            <Typography variant="body2">
              {t('helpWhatIsCustomProductDesc') || 'Products with customizable options like sizes, toppings, or add-ons.'}
            </Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: 56 }}>
                    <Box sx={{ bgcolor: 'primary.light', borderRadius: '50%', p: 1.5, display: 'flex', boxShadow: 1 }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: 24 } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {step.description}
                        </Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                          <Typography variant="caption" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'block' }}>
                            💡 {t('helpTips') || 'Tips'}
                          </Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: 'success.main', mt: 0.3 }} />
                              <Typography variant="body2">{tip}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ {t('helpImportantNotes') || 'Important Notes'}
            </Typography>
            <Typography variant="body2" component="div">
              <Box sx={{ mt: 1 }}>
                • {t('helpCustomImportantNote1') || 'Main product must have at least one price'}<br/>
                • {t('helpCustomImportantNote2') || 'Subproducts are optional but recommended'}<br/>
                • {t('helpCustomImportantNote3') || 'Each subproduct can have different currencies'}<br/>
                • {t('helpCustomImportantNote4') || 'Images are optional but improve sales'}<br/>
                • {t('helpCustomImportantNote5') || 'Maximum 8 additional product images allowed'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#f9f9f9' }}>
          <Button onClick={() => setHelpOpen(false)} variant="contained" color="primary" fullWidth={isMobile}>
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <HelpDialog />
      
      {/* ✅ COMPLETE Header */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between" 
        spacing={{ xs: 2, sm: 0 }}
        mb={{ xs: 2.5, sm: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          <InventoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
              {t('addCustomProduct') || 'Add Custom Product'}
            </Typography>
            {step === 2 && !isMobile && (
              <Chip 
                label={selectedCategoryName} 
                color="primary" 
                icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                onDelete={() => setStep(1)}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Stack>
        <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
          <Button
            variant="contained"
            color="info"
            startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            onClick={() => setHelpOpen(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': { boxShadow: 6 }
            }}
          >
            {t('helpButtonText') || 'Help'}
          </Button>
        </Tooltip>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('selectBranchToAddProduct') || 'Please select a branch first'}
        </Alert>
      )}

      {/* ✅ COMPLETE Step 1: Category Selection */}
      {step === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
              {t('selectCategoryHelp') || 'Select a category to get started'}
            </Typography>
          </Alert>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {categories.map(cat => (
              <Grid item xs={6} sm={4} md={3} key={cat.id || cat.Id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: { xs: 2, sm: 3 },
                    '&:hover': {
                      boxShadow: 6,
                      transform: isMobile ? 'none' : 'translateY(-4px)'
                    },
                    '&:active': {
                      transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)'
                    }
                  }}
                >
                  <CardActionArea onClick={() => handleCategorySelect(cat)}>
                    <CardContent sx={{ 
                      textAlign: 'center', 
                      py: { xs: 2, sm: 3 },
                      minHeight: { xs: 100, sm: 120 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <CategoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                        {cat.categoryName || cat.CategoryName}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ✅ COMPLETE Step 2: Product Details Form */}
      {step === 2 && (
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={{ xs: 2, sm: 3 }}>
            <IconButton onClick={() => setStep(1)} size={isMobile ? "small" : "medium"}>
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
              {t('productDetails') || 'Product Details'}
            </Typography>
            {isMobile && (
              <Chip label={selectedCategoryName} color="primary" size="small" sx={{ ml: 'auto', fontSize: '0.7rem' }} />
            )}
          </Stack>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Main Product Section Header */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.primary.main, 0.05), borderLeft: '4px solid', borderColor: 'primary.main' }}>
                  <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    <InventoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                    {t('mainProductInfo') || 'Main Product Information'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Product Name */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  {t('productName')} *
                </Typography>
                <TextField 
                  name="ProductName" 
                  value={product.ProductName} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  size={isMobile ? "small" : "medium"}
                  placeholder={t('enterProductName') || 'e.g., Custom Pizza'}
                />
              </Grid>

              {/* Prices Header */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  {t('prices')} * ({Object.keys(currencyPrices).length} {t('currencies') || 'currencies'})
                </Typography>
                <Alert severity="info" sx={{ py: { xs: 0.5, sm: 1 } }}>
                  💡 {t('addPricesInMultipleCurrencies') || 'Add prices in multiple currencies'}
                </Alert>
              </Grid>

              {/* Currency Prices */}
              {Object.keys(currencyPrices).map((currency) => {
                const currInfo = getCurrencyInfo(currency);
                return (
                  <Grid item xs={12} sm={6} md={4} key={currency}>
                    <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f9f9f9', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Chip label={`${currInfo.symbol} ${currency}`} color="primary" size="small" />
                        {Object.keys(currencyPrices).length > 1 && (
                          <IconButton size="small" color="error" onClick={() => handleRemoveCurrency(currency)}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </Box>
                      <TextField
                        fullWidth
                        type="number"
                        value={currencyPrices[currency]}
                        onChange={(e) => handlePriceChange(currency, e.target.value)}
                        inputProps={{ step: currInfo.decimals === 0 ? "1" : "0.01", min: "0" }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{currInfo.symbol}</InputAdornment>
                        }}
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {currInfo.name}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}

              {/* Add Currency Dropdown */}
              <Grid item xs={12}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <Select
                    value=""
                    onChange={(e) => handleAddCurrency(e.target.value)}
                    displayEmpty
                    startAdornment={<InputAdornment position="start"><AddIcon sx={{ color: 'primary.main' }} /></InputAdornment>}
                  >
                    <MenuItem value="" disabled><em>➕ {t('addAnotherCurrency') || 'Add Another Currency'}</em></MenuItem>
                    {currencies.filter(c => !currencyPrices[c.code]).map((curr) => (
                      <MenuItem key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.name} ({curr.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">{t('description')}</Typography>
                <TextField 
                  name="Description" 
                  value={product.Description} 
                  onChange={handleChange} 
                  fullWidth 
                  multiline
                  rows={isMobile ? 2 : 3}
                  size={isMobile ? "small" : "medium"}
                  placeholder={t('enterDescription') || 'Describe your product...'}
                />
              </Grid>

              {/* Ingredients & Active Status */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">{t('ingredients')}</Typography>
                <TextField 
                  name="Ingredients" 
                  value={product.Ingredients} 
                  onChange={handleChange} 
                  fullWidth 
                  size={isMobile ? "small" : "medium"}
                  placeholder={t('enterIngredients') || 'e.g., Flour, Tomatoes'}
                />
              </Grid>

              {/* CPAccountActive Toggle - Only show if product has subproducts */}
              {product.SubProducts && product.SubProducts.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    {t('cpAccountPricing') || 'Custom Product Account Pricing'}
                  </Typography>
                  <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.info.main, 0.05), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                    <FormControlLabel 
                      control={
                        <Checkbox 
                          checked={cpAccountActive} 
                          onChange={(e) => setCpAccountActive(e.target.checked)} 
                          size={isMobile ? "small" : "medium"} 
                        />
                      } 
                      label={
                        <Box>
                          <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, fontWeight: 'bold' }}>
                            {t('enableCPAccountPricing') || 'Enable CP Account Pricing'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('cpAccountPricingHint') || 'When enabled, subproduct prices will be used for CP account orders'}
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">{t('status')}</Typography>
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                  <FormControlLabel 
                    control={<Checkbox checked={product.IsActive} onChange={handleChange} name="IsActive" size={isMobile ? "small" : "medium"} />} 
                    label={<Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('active') || 'Active'}</Typography>}
                  />
                </Paper>
              </Grid>

              {/* Main Image Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">{t('mainProductImage') || 'Main Product Image'}</Typography>
                <Box sx={{ border: '2px dashed', borderColor: imagePreview ? 'success.main' : 'divider', p: { xs: 2, sm: 3 }, borderRadius: 2, textAlign: 'center', bgcolor: imagePreview ? alpha(theme.palette.success.main, 0.05) : '#fafafa' }}>
                  <input accept="image/*" style={{ display: 'none' }} id="main-product-image" type="file" onChange={handleImageChange} />
                  <label htmlFor="main-product-image">
                    <Button variant="contained" component="span" startIcon={<CloudUploadIcon />} color={imagePreview ? 'success' : 'primary'} size={isMobile ? "medium" : "large"} fullWidth={isMobile}>
                      {imagePreview ? `✓ ${t('mainImageSelected') || 'Main Image Selected'}` : t('uploadMainImage') || 'Upload Main Image'}
                    </Button>
                  </label>
                  {imagePreview && (
                    <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                      <Card sx={{ maxWidth: { xs: '100%', sm: 200 }, boxShadow: 3 }}>
                        <CardMedia component="img" image={imagePreview} alt="Main preview" sx={{ height: { xs: 200, sm: 150 }, objectFit: 'cover' }} />
                        <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 28, height: 28, zIndex: 1 }} onClick={handleRemoveMainImage}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Card>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Additional Product Images */}
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">{t('additionalProductImages') || 'Additional Product Images'}</Typography>
                  <Chip label={`${productImagePreviews.length}/8`} size="small" color={productImagePreviews.length >= 8 ? 'error' : 'primary'} />
                  <Chip label={t('optional') || 'Optional'} size="small" variant="outlined" />
                </Stack>
                <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  📸 {t('productImagesHint') || 'Upload up to 8 additional product images. These will be displayed in a gallery.'}
                </Alert>
                <Box sx={{ border: '2px dashed', borderColor: productImagePreviews.length > 0 ? 'info.main' : 'divider', p: { xs: 2, sm: 3 }, borderRadius: 2, textAlign: 'center', bgcolor: productImagePreviews.length > 0 ? alpha(theme.palette.info.main, 0.05) : '#fafafa' }}>
                  <input accept="image/*" style={{ display: 'none' }} id="product-images-upload" multiple type="file" onChange={handleProductImagesChange} disabled={productImagePreviews.length >= 8} />
                  <label htmlFor="product-images-upload">
                    <Button variant="contained" component="span" startIcon={<CollectionsIcon />} color={productImagePreviews.length > 0 ? 'info' : 'primary'} disabled={productImagePreviews.length >= 8} size={isMobile ? "medium" : "large"} fullWidth={isMobile}>
                      {productImagePreviews.length >= 8 ? `✓ ${t('maxImagesReached') || 'Max 8 Images'}` : productImagePreviews.length > 0 ? `✓ ${productImagePreviews.length} ${t('imagesSelected') || 'Images Selected'}` : t('uploadProductImages') || 'Upload Images (0/8)'}
                    </Button>
                  </label>
                  {productImagePreviews.length > 0 && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {productImagePreviews.map((src, index) => (
                        <Grid item xs={6} sm={4} md={2.4} key={index}>
                          <Card sx={{ position: 'relative', boxShadow: 3 }}>
                            <CardMedia component="img" image={src} alt={`Product Image ${index + 1}`} sx={{ height: { xs: 120, sm: 150 }, objectFit: 'cover' }} />
                            <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 24, height: 24 }} onClick={() => handleRemoveProductImage(index)}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 0.5, textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {t('image') || 'Image'} {index + 1}
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Grid>

              {/* Product Colors Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    🎨 {t('productColors') || 'Product Colors'}
                  </Typography>
                  <Chip label={`${productColors.length} ${t('colors') || 'colors'}`} size="small" color="secondary" />
                  <Chip label={t('optional') || 'Optional'} size="small" variant="outlined" />
                </Stack>
                <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  🎨 {t('colorsHint') || 'Add color variants for this product. Each color can have its own image.'}
                </Alert>
                <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), border: `2px dashed ${theme.palette.secondary.main}` }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 2 }}>
                    ➕ {t('addNewColor') || 'Add New Color'}
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                        {t('colorName') || 'Color Name'} *
                      </Typography>
                      <TextField fullWidth value={currentColor.colorName} onChange={handleColorNameChange} placeholder="e.g., Red, Blue" size={isMobile ? "small" : "medium"} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                        {t('colorImage') || 'Color Image'} ({t('optional') || 'Optional'})
                      </Typography>
                      <input accept="image/*" style={{ display: 'none' }} id="color-image-upload" type="file" onChange={handleColorImageChange} />
                      <label htmlFor="color-image-upload">
                        <Button variant="outlined" component="span" fullWidth startIcon={<PhotoCameraIcon />} size={isMobile ? "small" : "medium"}>
                          {currentColor.colorImagePreview ? '✓ Image Selected' : 'Upload Image'}
                        </Button>
                      </label>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button variant="contained" color="secondary" onClick={handleAddColor} fullWidth startIcon={<AddIcon />} disabled={!currentColor.colorName.trim()} size={isMobile ? "small" : "medium"}>
                        {t('addColor') || 'Add Color'}
                      </Button>
                    </Grid>
                    {currentColor.colorImagePreview && (
                      <Grid item xs={12}>
                        <Card sx={{ maxWidth: 200, boxShadow: 2 }}>
                          <CardMedia component="img" image={currentColor.colorImagePreview} alt={currentColor.colorName} sx={{ height: 120, objectFit: 'cover' }} />
                          <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.6)', textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'white' }}>{t('preview') || 'Preview'}</Typography>
                          </Box>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
                {productColors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      📋 {t('addedColors') || 'Added Colors'} ({productColors.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {productColors.map((color, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Card sx={{ position: 'relative', boxShadow: 3, border: `2px solid ${theme.palette.secondary.main}` }}>
                            <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'error.main', color: 'white', zIndex: 1, '&:hover': { bgcolor: 'error.dark' }, width: 28, height: 28 }} onClick={() => handleRemoveColor(index)}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            {color.colorImagePreview ? (
                              <CardMedia component="img" image={color.colorImagePreview} alt={color.colorName} sx={{ height: { xs: 120, sm: 150 }, objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{ height: { xs: 120, sm: 150 }, bgcolor: alpha(theme.palette.secondary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PaletteIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: alpha(theme.palette.secondary.main, 0.3) }} />
                              </Box>
                            )}
                            <CardContent sx={{ p: { xs: 1, sm: 1.5 }, bgcolor: 'secondary.main', textAlign: 'center' }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', fontSize: { xs: '0.8rem', sm: '0.9rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                🎨 {color.colorName}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                {productColors.length === 0 && (
                  <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    ℹ️ {t('noColorsAdded') || 'No colors added yet. Add colors above to give customers options.'}
                  </Alert>
                )}
              </Grid>

              {/* ✅ NEW: Product Sizes Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    📏 {t('productSizes') || 'Product Sizes'}
                  </Typography>
                  <Chip 
                    label={`${productSizes.length} ${t('sizes') || 'sizes'}`} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#ed6c02', 
                      color: 'white',
                      fontWeight: 'bold'
                    }} 
                  />
                  <Chip label={t('optional') || 'Optional'} size="small" variant="outlined" />
                </Stack>
                <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.85rem' } }} icon={<StraightenIcon sx={{ fontSize: 20 }} />}>
                  📏 {t('sizesHint') || 'Add size variants like S, M, L, XL for this product.'}
                </Alert>

                {/* Add Size Form */}
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: 3, 
                    bgcolor: alpha('#ed6c02', 0.05), 
                    border: `2px dashed #ed6c02` 
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 2 }}>
                    ➕ {t('addNewSize') || 'Add New Size'}
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                        {t('sizeName') || 'Size Name'} *
                      </Typography>
                      <TextField 
                        fullWidth 
                        value={currentSizeName} 
                        onChange={(e) => setCurrentSizeName(e.target.value)} 
                        placeholder={t('enterSizeName') || 'e.g., S, M, L, XL, XXL'}
                        size={isMobile ? "small" : "medium"}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <StraightenIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                            </InputAdornment>
                          )
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddSize();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button 
                        variant="contained" 
                        onClick={handleAddSize} 
                        fullWidth 
                        startIcon={<AddIcon />} 
                        disabled={!currentSizeName.trim()} 
                        size={isMobile ? "small" : "medium"}
                        sx={{ 
                          bgcolor: '#ed6c02', 
                          '&:hover': { bgcolor: '#e65100' },
                          '&.Mui-disabled': { bgcolor: alpha('#ed6c02', 0.3) }
                        }}
                      >
                        {t('addSize') || 'Add Size'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Display Added Sizes */}
                {productSizes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      📋 {t('addedSizes') || 'Added Sizes'} ({productSizes.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {productSizes.map((size, index) => (
                        <Chip
                          key={index}
                          label={size.sizeName}
                          onDelete={() => handleRemoveSize(index)}
                          deleteIcon={
                            <CloseIcon sx={{ fontSize: 16, color: 'white !important' }} />
                          }
                          icon={<StraightenIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                          sx={{
                            bgcolor: '#ed6c02',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            height: { xs: 36, sm: 40 },
                            px: 1,
                            boxShadow: '0 2px 6px rgba(237, 108, 2, 0.35)',
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(255,255,255,0.7)',
                              '&:hover': { color: 'white' }
                            },
                            '&:hover': {
                              bgcolor: '#e65100',
                            },
                            transition: 'all 0.2s ease'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {productSizes.length === 0 && (
                  <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    ℹ️ {t('noSizesAdded') || 'No sizes added yet. Add sizes above to give customers options.'}
                  </Alert>
                )}
              </Grid>

              {/* Subproducts Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderLeft: '4px solid', borderColor: 'secondary.main', mt: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={{ xs: 1.5, sm: 0 }}>
                    <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      <LocalOfferIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                      {t('subproducts') || 'Subproducts'} ({product.SubProducts.length})
                    </Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddSubProduct} color="primary" size={isMobile ? "small" : "medium"} fullWidth={isMobile}>
                      {t('addSubproduct') || 'Add Subproduct'}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>

              {/* ✅ COMPLETE Subproducts List */}
              {product.SubProducts.map((sp, idx) => (
                <Grid item xs={12} key={idx}>
                  <Paper elevation={2} sx={{ border: '2px solid', borderColor: 'secondary.light', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.secondary.main, 0.1), display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: isMobile ? 'pointer' : 'default' }} onClick={() => isMobile && toggleSubProductExpand(idx)}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label={`${t('subproduct') || 'Subproduct'} #${idx + 1}`} color="secondary" icon={<LocalOfferIcon sx={{ fontSize: 14 }} />} size="small" />
                        {sp.Name && <Typography variant="caption" fontWeight="bold" sx={{ display: { xs: 'none', sm: 'block' } }}>{sp.Name}</Typography>}
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        {isMobile && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleSubProductExpand(idx); }}>
                            <ExpandMoreIcon sx={{ fontSize: 20, transform: expandedSubProducts[idx] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                          </IconButton>
                        )}
                        <IconButton color="error" onClick={(e) => { e.stopPropagation(); handleRemoveSubProduct(idx); }} size="small">
                          <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        </IconButton>
                      </Stack>
                    </Box>
                    <Collapse in={!isMobile || expandedSubProducts[idx]} timeout="auto">
                      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          {/* Subproduct Name */}
                          <Grid item xs={12} sm={6}>
                            <TextField label={t('subproductName') || 'Name *'} value={sp.Name} onChange={e => handleSubProductChange(idx, 'Name', e.target.value)} fullWidth required size="small" placeholder="e.g., Large Size" />
                          </Grid>
                          
                          {/* Description */}
                          <Grid item xs={12} sm={6}>
                            <TextField label={t('description') || 'Description'} value={sp.Description} onChange={e => handleSubProductChange(idx, 'Description', e.target.value)} fullWidth size="small" />
                          </Grid>

                          {/* Currency Prices Header */}
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t('prices')} ({Object.keys(sp.CurrencyPrices || {}).length} {t('currencies')})
                            </Typography>
                          </Grid>

                          {/* Display Subproduct Currency Prices */}
                          {Object.keys(sp.CurrencyPrices || {}).map((currency) => {
                            const currInfo = getCurrencyInfo(currency);
                            return (
                              <Grid item xs={12} sm={6} md={4} key={`sub-${idx}-${currency}`}>
                                <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f0f0f0', borderLeft: '3px solid', borderColor: 'secondary.main' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Chip label={`${currInfo.symbol} ${currency}`} color="secondary" size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                                    {Object.keys(sp.CurrencyPrices).length > 1 && (
                                      <IconButton size="small" color="error" onClick={() => handleRemoveSubProductCurrency(idx, currency)}>
                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    )}
                                  </Box>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    size="small"
                                    value={sp.CurrencyPrices[currency]}
                                    onChange={(e) => handleSubProductPriceChange(idx, currency, e.target.value)}
                                    inputProps={{ step: currInfo.decimals === 0 ? "1" : "0.01", min: "0" }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">{currInfo.symbol}</InputAdornment> }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}

                          {/* Add Currency for Subproduct */}
                          <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                              <Select value="" onChange={(e) => handleAddSubProductCurrency(idx, e.target.value)} displayEmpty>
                                <MenuItem value="" disabled><em>➕ {t('addCurrencyToSubproduct') || 'Add Currency'}</em></MenuItem>
                                {currencies.filter(c => !sp.CurrencyPrices[c.code]).map((curr) => (
                                  <MenuItem key={curr.code} value={curr.code}>{curr.symbol} {curr.name} ({curr.code})</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Size, Height, Width */}
                          <Grid item xs={6} sm={3}>
                            <TextField label={t('sizeType') || 'Size'} value={sp.SizeType} onChange={e => handleSubProductChange(idx, 'SizeType', e.target.value)} fullWidth size="small" placeholder="XL" />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField label={t('height') || 'Height'} value={sp.Height} onChange={e => handleSubProductChange(idx, 'Height', e.target.value)} fullWidth type="number" size="small" />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField label={t('width') || 'Width'} value={sp.Width} onChange={e => handleSubProductChange(idx, 'Width', e.target.value)} fullWidth type="number" size="small" />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControlLabel control={<Checkbox checked={!!sp.FixedQuantity} onChange={e => handleSubProductChange(idx, 'FixedQuantity', e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.85rem' }}>{t('fixedQuantity') || 'Fixed Qty'}</Typography>} />
                          </Grid>

                          {/* Image Upload & Active Status */}
                          <Grid item xs={12} sm={6}>
                            <input accept="image/*" style={{ display: 'none' }} id={`subproduct-image-${idx}`} type="file" onChange={e => e.target.files[0] && handleSubProductImage(idx, e.target.files[0])} />
                            <label htmlFor={`subproduct-image-${idx}`}>
                              <Button variant="outlined" component="span" startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />} size="small" fullWidth color={sp.Image ? 'success' : 'secondary'}>
                                {sp.Image ? `✓ ${t('imageSelected') || 'Image Selected'}` : t('uploadImage') || 'Upload Image'}
                              </Button>
                            </label>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper elevation={0} sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                              <FormControlLabel control={<Checkbox checked={sp.IsActive} onChange={e => handleSubProductChange(idx, 'IsActive', e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.85rem' }}>{t('active') || 'Active'}</Typography>} />
                            </Paper>
                          </Grid>

                          {/* Subproduct Image Preview */}
                          {sp.Image && (
                            <Grid item xs={12}>
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Card sx={{ maxWidth: { xs: 150, sm: 120 }, boxShadow: 2 }}>
                                  <CardMedia component="img" image={sp.Image} alt="subproduct preview" sx={{ height: { xs: 120, sm: 100 }, objectFit: 'cover' }} />
                                </Card>
                                <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 24, height: 24, zIndex: 1 }} onClick={() => handleRemoveSubProductImage(idx)}>
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              ))}

              {/* Submit Buttons */}
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 2, sm: 3 } }}>
                  <Button type="submit" variant="contained" color="success" disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />} fullWidth={isMobile} sx={{ px: { xs: 3, sm: 4 }, py: { xs: 1.2, sm: 1.5 }, fontWeight: 'bold', flex: 1, boxShadow: 3 }}>
                    {loading ? (t('creating') || 'Creating...') : (t('createProduct') || 'Create Product')}
                  </Button>
                  <Button variant="outlined" color="error" size={isMobile ? "medium" : "large"} onClick={() => setStep(1)} fullWidth={isMobile}>
                    {t('cancel') || 'Cancel'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {/* Snackbar Notification */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}